import { defineStore } from 'pinia';
import {
  trainingCentresDb,
  type TrainingCentreRow,
  uniqueProgramsFromTrainingCentres,
} from '@/db/trainingCentres';
import { getLocaleFromPath } from '@/i18n';

const API_BASE = '';

function publicTrainingCentresListUrl(): string {
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const locale = getLocaleFromPath(path);
  return `${API_BASE}/api/v2/public-organisations/?locale=${encodeURIComponent(locale)}`;
}

const META_ID = 'default' as const;

export type TrainingCentresStatus = 'uninitialized' | 'loading' | 'ready' | 'error';

interface PublicTrainingCentresApiResponse {
  meta: { total_count: number };
  items: TrainingCentreRow[];
}

function sortByName(a: TrainingCentreRow, b: TrainingCentreRow): number {
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}

export const useTrainingCentresStore = defineStore('trainingCentres', {
  state: () => ({
    status: 'uninitialized' as TrainingCentresStatus,
    error: null as string | null,
    items: [] as TrainingCentreRow[],
    etag: null as string | null,
    totalCount: 0 as number,
  }),

  getters: {
    sortedItems: (state): TrainingCentreRow[] =>
      [...state.items].sort(sortByName),

    byId: (state): Record<number, TrainingCentreRow> => {
      const out: Record<number, TrainingCentreRow> = {};
      for (const row of state.items) {
        out[row.id] = row;
      }
      return out;
    },
  },

  actions: {
    async hydrateFromCache(): Promise<void> {
      const meta = await trainingCentresDb.meta.get(META_ID);
      const rows = await trainingCentresDb.trainingCentre.toArray();
      this.etag = meta?.etag ?? null;
      this.totalCount = meta?.totalCount ?? rows.length;
      this.items = rows.sort(sortByName);
    },

    async fetchFromApi(): Promise<void> {
      const hadCached = this.items.length > 0;
      if (!hadCached) {
        this.status = 'loading';
      }
      this.error = null;

      const headers: HeadersInit = {};
      if (this.etag) {
        headers['If-None-Match'] = this.etag;
      }

      try {
        const response = await fetch(publicTrainingCentresListUrl(), {
          credentials: 'same-origin',
          headers,
        });

        const etag = response.headers.get('etag') ?? response.headers.get('ETag');

        if (response.status === 304) {
          if (etag) this.etag = etag;
          this.status = 'ready';
          return;
        }

        if (!response.ok) {
          this.error = `HTTP ${response.status}`;
          this.status = hadCached ? 'ready' : 'error';
          return;
        }

        const body = (await response.json()) as PublicTrainingCentresApiResponse;
        const items = body.items ?? [];
        const totalCount = body.meta?.total_count ?? items.length;

        try {
          await trainingCentresDb.transaction(
            'rw',
            trainingCentresDb.trainingCentre,
            trainingCentresDb.program,
            trainingCentresDb.meta,
            async () => {
              await trainingCentresDb.trainingCentre.clear();
              await trainingCentresDb.program.clear();
              if (items.length > 0) {
                await trainingCentresDb.trainingCentre.bulkPut(items);
                const programs = uniqueProgramsFromTrainingCentres(items);
                if (programs.length > 0) {
                  await trainingCentresDb.program.bulkPut(programs);
                }
              }
              await trainingCentresDb.meta.put({
                id: META_ID,
                etag: etag ?? null,
                fetchedAt: Date.now(),
                totalCount,
              });
            }
          );
        } catch {
          // Ignore cache write failures
        }

        this.items = [...items].sort(sortByName);
        this.etag = etag ?? null;
        this.totalCount = totalCount;
        this.status = 'ready';
      } catch (e) {
        this.error = e instanceof Error ? e.message : String(e);
        this.status = hadCached ? 'ready' : 'error';
      }
    },

    async initialize(): Promise<void> {
      await this.hydrateFromCache();
      if (this.items.length > 0) {
        this.status = 'ready';
      }
      await this.fetchFromApi();
      if (this.status === 'error' && this.items.length > 0) {
        this.status = 'ready';
      }
    },

    /** Keep directory + Dexie in sync after a fresh organisation detail fetch (list may still be 304-cached). */
    applyDetailOrganisation(row: TrainingCentreRow): void {
      const idx = this.items.findIndex((r) => r.id === row.id);
      if (idx >= 0) {
        const next = [...this.items];
        next[idx] = row;
        this.items = next;
      }
      try {
        void trainingCentresDb.trainingCentre.put(row);
      } catch {
        // Ignore cache write failures
      }
    },
  },
});
